import type { ReachingGroup } from '@/domain/access';
import { checklistOf } from '@/domain/checklist';
import type { Priority, Verdict, Visibility } from '@/domain/constants';
import type { Rollup } from '@/domain/health';
import type { FieldValue } from '@/domain/module-kit';
import type { TagDef } from '@/domain/tags';
import type { WorkItemState } from '@/domain/types';
import type { NodeBrief } from './board';
import type { PrimaryStage } from './flows';
import type { ListRow } from './list';

/**
 * The ITEM RECORD's SHAPE and its pure projections (docs/SYNC.md §7.1): the one
 * description of what a device holds about an item, and the mappings every
 * surface renders it through.
 *
 * This file is deliberately I/O-FREE and imports nothing but types and pure
 * domain functions (the list's reader, `checklistOf`), so the CLIENT can
 * import it: `src/components/live-store.ts` holds records, projects
 * list rows and patches board cards through exactly these functions, and
 * `src/read/records.ts` produces them. One shape end to end - a second copy in
 * `src/components` was the slice-3 stand-in and is gone.
 *
 * `ItemRecord` carries `Date`s (the read layer's shape); `LiveRecord` is the
 * same record with the two dates as epoch millis - what crosses the wire
 * (§5.5) and what the device stores, since JSON and IndexedDB have no `Date`.
 */

/** The neighbourhood a record carries: ids, titles and glyphs of the VISIBLE
 *  items one hop out, by relation. Decomposition up/down (`parent` edges),
 *  the hard dependency both ways (`blocks`), and the soft one (`relates`). */
export interface RecordNeighbours {
  parents: NodeBrief[];
  children: NodeBrief[];
  /** Items this one is blocked BY (the `blocks` edges it is the from end of). */
  blockers: NodeBrief[];
  /** Items this one blocks (the `blocks` edges it is the to end of). */
  blocking: NodeBrief[];
  relates: NodeBrief[];
}

export interface ItemRecord {
  // --- everything a `ListRow` carries, same names, same types ---------------
  id: string;
  title: string;
  verdict: Verdict;
  priority: Priority | null;
  delivered: boolean;
  active: boolean;
  closed: boolean;
  contributors: string[];
  requesters: string[];
  requester: string | null;
  primaryFlowName: string | null;
  primaryFlowId: string | null;
  primaryFlowPhaseIndex: number | null;
  primaryFlowPhaseKey: string | null;
  /** The groups that reach the item, as `ListRow.groups`. */
  groups: ReachingGroup[];
  updatedAt: Date;
  createdAt: Date;
  tags: TagDef[];
  parent: { id: string; title: string } | null;
  parents: { id: string; title: string }[];
  /** Every visible ancestor's id, as `ListRow.ancestorIds`. A record held
   *  from before the field existed lacks it on the device; readers default
   *  it to none. */
  ancestorIds: string[];
  /** The effective group ids, as `ListRow.groupIds`. A record held from
   *  before the field existed lacks it on the device; readers default it to
   *  none. */
  groupIds: string[];
  /** The user tags, as spelled (docs/FIELDS_AND_TAGS.md §6). A record held
   *  from before the field existed lacks it on the device; readers default it
   *  to none, so such a record matches no tag filter until refreshed. */
  itemTags: string[];
  path: { id: string; title: string }[];
  dueAt: string | null;
  canAct: boolean;
  /** The weights of docs/RESOLUTION.md §10 (`src/read/weights.ts`). */
  openChildren: number;
  openBlockers: number;
  /** The list digest's two halves (`ListRow.childCount`, `ListRow.lineTally`).
   *  A record held from before the fields existed lacks them on the device;
   *  readers default them to none. */
  childCount: number;
  lineTally: { total: number; ticked: number };
  fieldValues: Record<string, FieldValue>;
  // --- and what only the record has ----------------------------------------
  orgId: string;
  visibility: Visibility;
  description: string | null;
  acceptanceCriteria: string | null;
  note: string | null;
  moduleData: Record<string, unknown>;
  /** Who concluded it, from the LOG (the last concluding event's principal, an
   *  actor ref - never a name): null when the item is open, or when no
   *  conclusion is in the log's retained history. */
  closedBy: string | null;
  neighbours: RecordNeighbours;
  /** The subtree rollup the item page shows (root included), or null for a
   *  leaf - a record with no visible descendants rolls up nothing. */
  rollup: Rollup | null;
  /** The item's PRIMARY flow position, the board card's lifecycle word. Not on
   *  a `ListRow` (which flattens it to the three `primaryFlow*` fields); the
   *  record keeps it whole so a board card can be projected. */
  primaryStage: PrimaryStage | null;
}

/**
 * An ITEM RECORD on the wire and in the device store (docs/SYNC.md §5.5,
 * §7.1): its `Date` fields flattened to epoch millis, since JSON has none. The
 * client rehydrates and projects (a list row, a board card) from the record it
 * holds.
 */
export interface LiveRecord extends Omit<ItemRecord, 'updatedAt' | 'createdAt'> {
  updatedAt: number;
  createdAt: number;
}

/** An `ItemRecord` -> its wire form: the two `Date`s become epoch millis. Pure. */
export function toLiveRecord(record: ItemRecord): LiveRecord {
  return {
    ...record,
    updatedAt: record.updatedAt.getTime(),
    createdAt: record.createdAt.getTime(),
  };
}

/** The wire form -> the record: the two epoch-ms fields become `Date`s. Pure. */
export function fromLiveRecord(record: LiveRecord): ItemRecord {
  return {
    ...record,
    updatedAt: new Date(record.updatedAt),
    createdAt: new Date(record.createdAt),
  };
}

/** The row fields a record carries, over whichever clock the caller holds: the
 *  ONE field list behind both list projections, so the two cannot drift. */
type RowFields<D> = Omit<ItemRecord, 'updatedAt' | 'createdAt'> & {
  updatedAt: D;
  createdAt: D;
};

function listRowFrom<D>(r: RowFields<D>, at: (d: D) => Date): ListRow {
  const row: ListRow = {
    id: r.id,
    title: r.title,
    verdict: r.verdict,
    priority: r.priority,
    delivered: r.delivered,
    active: r.active,
    closed: r.closed,
    contributors: r.contributors,
    requesters: r.requesters,
    requester: r.requester,
    primaryFlowName: r.primaryFlowName,
    primaryFlowId: r.primaryFlowId,
    primaryFlowPhaseIndex: r.primaryFlowPhaseIndex,
    primaryFlowPhaseKey: r.primaryFlowPhaseKey,
    groups: r.groups,
    updatedAt: at(r.updatedAt),
    createdAt: at(r.createdAt),
    tags: r.tags,
    parent: r.parent,
    parents: r.parents,
    ancestorIds: r.ancestorIds ?? [],
    groupIds: r.groupIds ?? [],
    itemTags: r.itemTags ?? [],
    path: r.path,
    dueAt: r.dueAt,
    canAct: r.canAct,
    openChildren: r.openChildren,
    openBlockers: r.openBlockers,
    childCount: r.childCount ?? 0,
    lineTally: r.lineTally ?? { total: 0, ticked: 0 },
    // The record holds the module data, so its row carries the list itself:
    // a line ticked on another phone arrives with the pushed record.
    checklist: checklistOf(r.moduleData),
    // As in `listingToRow`: a record carries no stage `means`, so the review
    // duty rides only the list read (the board item "Resolution build").
    reviewRequired: false,
    fieldValues: r.fieldValues,
  };
  // `closedBy` stays ABSENT rather than null when nothing concluded it: the row
  // shape says undefined = "no concluding act named" (docs/SYNC.md §7.3).
  if (r.closedBy !== null) row.closedBy = r.closedBy;
  return row;
}

/** A record -> the list table's row shape. The one mapping every list-shaped
 *  surface (the Work list, a saved list, the checklist, a lens rail) renders,
 *  so the pushed row and a fresh render cannot drift. Pure. */
export function recordToListRow(r: ItemRecord): ListRow {
  return listRowFrom(r, (d) => d);
}

/** The same projection over the WIRE record the device holds. Pure. */
export function liveRecordToListRow(r: LiveRecord): ListRow {
  return listRowFrom(r, (ms) => new Date(ms));
}

/**
 * The per-board half of a board card - everything that is the BOARD's reading
 * of an item rather than the item's own state, so it cannot come from a record:
 * where the card sits on this board (`rank`, `laneId`, `pinned`, `pulled`), what
 * this board's own fact rules said (`lensSignals`), the digest count and the
 * highlight row the board's primary flow designates, and the seat offers
 * addressed to the viewer. Plus the two fields of `WorkItemState` the record
 * does not carry - the FULL owner set and `inheritsAccess` (see
 * `recordToBoardItem`).
 */
export interface BoardCardContext {
  requesters: string[];
  inheritsAccess: boolean;
  children: NodeBrief[];
  pulled: boolean;
  digestCount: number;
  rank: string | null;
  laneId: string;
  pinned: boolean;
}

/** The item-shaped half of a board card: `BoardItem`
 *  (src/app/boards/dto.ts) minus the fields the PAGE owns - the
 *  resolved display identities and the board-local `offeredRoles` /
 *  `lensSignals` / `highlightFields`. The app spreads this and adds those. */
export interface RecordBoardItem {
  id: string;
  state: WorkItemState;
  parent: { id: string; title: string } | null;
  /** Every visible parent (`parent` is the first): the `parent` filter's fact. */
  parents: { id: string; title: string }[];
  /** The user tags (docs/FIELDS_AND_TAGS.md §6): the `tag` filter's fact. */
  itemTags: string[];
  children: NodeBrief[];
  pulled: boolean;
  tags: TagDef[];
  digestCount: number;
  rank: string | null;
  laneId: string;
  pinned: boolean;
  primaryStage: PrimaryStage | null;
}

/**
 * A record + this board's reading of it -> the item half of a board card. The
 * card's own display identities (`requester`/`contributors` as resolved
 * `Actor`s) and the board-local `offeredRoles` / `lensSignals` /
 * `highlightFields` stay with the page, which holds the directory; this
 * projection produces the item-shaped part the record can supply.
 *
 * THE GAP (reported, not papered over): `WorkItemState` needs the full
 * `requesters` array and `inheritsAccess`, and a record carries neither (it
 * flattens the owner set to its primary, and access-inheritance is a gate
 * input, not a surface). Both ride in `BoardCardContext` until a widening of
 * the record is settled. Pure.
 */
export function recordToBoardItem(r: ItemRecord, ctx: BoardCardContext): RecordBoardItem {
  return {
    id: r.id,
    state: {
      verdict: r.verdict,
      delivered: r.delivered,
      active: r.active,
      closed: r.closed,
      requesters: ctx.requesters,
      contributors: r.contributors,
      priority: r.priority,
      acceptanceCriteria: r.acceptanceCriteria,
      description: r.description,
      note: r.note,
      visibility: r.visibility,
      inheritsAccess: ctx.inheritsAccess,
      title: r.title,
    },
    parent: r.parent,
    parents: r.parents,
    itemTags: r.itemTags ?? [],
    children: ctx.children,
    pulled: ctx.pulled,
    tags: r.tags,
    digestCount: ctx.digestCount,
    rank: ctx.rank,
    laneId: ctx.laneId,
    pinned: ctx.pinned,
    primaryStage: r.primaryStage,
  };
}
